/**
 * UpdateRenderer — read-only render for UPDATE statements. Handles the plain
 * UPDATE form, `UPDATE … FROM …` (PG / MSSQL), and `UPDATE … JOIN …` /
 * `UPDATE t1, t2 …` (MySQL). Scaffolds every § 1.4 disabled control so
 * VQE-Edit lights them up later by passing real handlers.
 *
 * Section layout:
 *   - Update target card (always)
 *   - Tables section (only when a multi-table tail is present — MySQL form)
 *   - SET assignments
 *   - From/Source section (only when PG / MSSQL UPDATE…FROM)
 *   - Where pills — or, with no WHERE, a red one-line WHERE row (and a red
 *     "No WHERE" badge on the target row) saying every row changes
 *   - Returning card (when present)
 *
 * If parsing fails (no target), renders a small fallback
 * surface — the Visual view still offers Edit as SQL.
 */

import type { ParseOptions, StatementInfo } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { DmlChangeTablePicker } from '../common/DmlChangeTablePicker';
import { DmlNoWhereBadge } from '../common/DmlNoWhereBadge';
import { DmlTargetCard } from '../common/DmlTargetCard';
import { dmlWhereScope } from '../common/dmlWhereScope';
import { DmlParseErrorPanel } from '../fallback/DmlParseErrorPanel';
import { ReturningCard } from '../insert/ReturningCard';
import { ClauseCard } from '../select/ClauseCard';
import { vqeCopy } from '../../ux/vqeCopy';
import { FilterPills } from '../select/FilterPills';
import { extractUpdateOutline } from './extractUpdateOutline';
import {
  buildAliasCandidatesFromText,
  formatDmlTargetText,
  readBareAliasTail,
  resolveDmlTargetAlias,
} from './resolveDmlTargetAlias';
import { SetAssignmentsList } from './SetAssignmentsList';
import { TablesWithJoinsCard } from './TablesWithJoinsCard';
import { FULL_CANVAS_ROOT_CLASS } from '../common/canvasLayout';

/** DML-17: MySQL's "TABLES" clause is the same concept as FROM elsewhere. */
const MYSQL_TABLES_TITLE = "Tables — this statement's FROM";

export interface UpdateRendererProps {
  readonly statement: StatementInfo;
  readonly bufferText: string;
  readonly options: ParseOptions;
  /** Escape to raw SQL. Surfaced on the parse-error panel; the workspace always threads it. */
  readonly onEditAsSql?: () => void;
  readonly drillTabId?: string;
  readonly editContext?: VqeEditContext;
}

export function UpdateRenderer({
  statement,
  bufferText,
  options,
  onEditAsSql,
  editContext,
}: UpdateRendererProps) {
  const outline = extractUpdateOutline(bufferText, statement.range, options);

  if (!outline.hasUpdate || outline.targetText === null) {
    return <DmlParseErrorPanel kind="update" onEditAsSql={onEditAsSql ?? (() => undefined)} />;
  }

  const hasMultiTableRaw =
    outline.multiTableStart !== null && outline.multiTableEnd !== null;
  const hasFrom = outline.fromStart !== null && outline.fromEnd !== null;
  const hasWhere = outline.whereStart !== null && outline.whereEnd !== null;
  const hasReturning =
    outline.returningStart !== null && outline.returningEnd !== null;
  const whereScope = dmlWhereScope(outline);

  // DML-14/15/17/19 — the render-level outline walker doesn't know which
  // token is a table vs. an alias, so an mssql `UPDATE p … FROM
  // Production.Product p` shows a bare `p` pill, and a MySQL/Oracle
  // target's own trailing alias leaks into the multi-table tail as an
  // information-free lone row. Resolve both against the FROM/TABLES tail.
  let targetPillText = outline.targetText;
  let multiTableBareAlias: ReturnType<typeof readBareAliasTail>['candidate'] = null;
  let multiTableHasJoins = false;
  // MSSQL `UPDATE p … FROM Production.Product p`: the target token is an alias
  // of a FROM table, so rewriting it would point the UPDATE at a new table
  // cross-joined with the old one — Change table is blocked for that shape.
  let targetIsFromAlias = false;

  if (hasFrom && outline.fromStart !== null && outline.fromEnd !== null) {
    const candidates = buildAliasCandidatesFromText(
      bufferText,
      outline.fromStart,
      outline.fromEnd,
      options,
    );
    const resolved = resolveDmlTargetAlias(outline.targetText, candidates);
    targetPillText = formatDmlTargetText(outline.targetText, resolved);
    targetIsFromAlias =
      resolved.relation !== null &&
      resolved.alias !== null &&
      resolved.alias.toLowerCase() === outline.targetText.trim().toLowerCase();
  } else if (hasMultiTableRaw && outline.multiTableStart !== null && outline.multiTableEnd !== null) {
    const tail = readBareAliasTail(
      bufferText,
      outline.multiTableStart,
      outline.multiTableEnd,
      options,
    );
    multiTableBareAlias = tail.candidate;
    multiTableHasJoins = tail.hasJoins;
    if (tail.candidate) {
      const resolved = resolveDmlTargetAlias(outline.targetText, [tail.candidate]);
      if (!multiTableHasJoins) {
        // Oracle DML-19: a multi-table tail that's nothing but the target's
        // own alias (no real JOIN) adds no information as a standalone
        // "Tables" row — merge it into the target card instead.
        targetPillText = formatDmlTargetText(outline.targetText, resolved);
      }
    }
  }

  // Merge only when there's real alias info AND no further JOIN content —
  // otherwise (MySQL) the Tables card still earns its keep as a JOIN summary.
  const mergeMultiTableIntoTarget = multiTableBareAlias !== null && !multiTableHasJoins;
  const showTablesCard = hasMultiTableRaw && !mergeMultiTableIntoTarget;
  const tablesStart =
    multiTableBareAlias !== null && outline.targetStart !== null
      ? outline.targetStart
      : outline.multiTableStart;

  return (
    <div
      className={FULL_CANVAS_ROOT_CLASS}
      data-testid="vqe-update-renderer"
      data-has-multi-table={showTablesCard ? 'true' : 'false'}
      data-has-from={hasFrom ? 'true' : 'false'}
      data-has-where={hasWhere ? 'true' : 'false'}
      data-has-returning={hasReturning ? 'true' : 'false'}
    >
      <DmlTargetCard
        intent="update"
        variant="full"
        targetText={targetPillText}
        targetAction={
          <>
            <DmlNoWhereBadge verb="UPDATE" targetText={targetPillText} scope={whereScope} />
            <DmlChangeTablePicker
              target="update"
              editContext={editContext}
              blockedReason={targetIsFromAlias ? vqeCopy.dmlTarget.disabled.aliasTarget : undefined}
            />
          </>
        }
      />

      {showTablesCard && tablesStart !== null && outline.multiTableEnd !== null ? (
        <TablesWithJoinsCard
          bufferText={bufferText}
          start={tablesStart}
          end={outline.multiTableEnd}
          options={options}
          title={MYSQL_TABLES_TITLE}
          testIdPrefix="vqe-update-tables"
          fallbackLeadingText={outline.targetText}
        />
      ) : null}

      {outline.setStart !== null && outline.setEnd !== null ? (
        <ClauseCard title="Set" testId="vqe-update-set">
          <SetAssignmentsList
            bufferText={bufferText}
            start={outline.setStart}
            end={outline.setEnd}
            options={options}
            testIdPrefix="vqe-update-set"
            editContext={editContext}
          />
        </ClauseCard>
      ) : null}

      {hasFrom && outline.fromStart !== null && outline.fromEnd !== null ? (
        <TablesWithJoinsCard
          bufferText={bufferText}
          start={outline.fromStart}
          end={outline.fromEnd}
          options={options}
          title="From"
          testIdPrefix="vqe-update-from"
        />
      ) : null}

      {/* With no WHERE this is the one-line red WHERE row: it says every row
          changes and carries the Add condition that fixes it. */}
      <FilterPills
        bufferText={bufferText}
        span={
          hasWhere && outline.whereStart !== null && outline.whereEnd !== null
            ? { start: outline.whereStart, end: outline.whereEnd }
            : null
        }
        options={options}
        kind="where"
        editContext={editContext}
        density="compact"
        destructiveGuard={{ verb: 'UPDATE', targetText: targetPillText, scope: whereScope }}
      />

      {hasReturning && outline.returningStart !== null && outline.returningEnd !== null ? (
        <ReturningCard
          bufferText={bufferText}
          start={outline.returningStart}
          end={outline.returningEnd}
          options={options}
          testIdPrefix="vqe-update-returning"
        />
      ) : null}
    </div>
  );
}
